import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Home from "./Pages/Home";
import About from "./Pages/About";
import Projects from "./Pages/Projects";
import Experience from "./Components/Experience";
import Form from "./Components/Forms";
import Footer from "./Components/Footer";
import ProjectDetail from "./Components/ProjectDetail";
import PageTransition from "./Components/PageTransition";
import PremiumLoadingScreen from "./Components/PremiumLoadingScreen";
import NeuralNetworkBackground from "./Components/NeuralNetworkBackground";
import AICodeMatrix from "./Components/AICodeMatrix";
import FloatingAIBadges from "./Components/FloatingAIBadges";
import Meta, { HOME_META } from './Components/Meta';
import ParticleCursor from "./Components/ParticleCursor";
import Tech from '../src/Components/Tech';
import ThemeToggle from './Components/ThemeToggle';
import AdvancedSearch from './Components/AdvancedSearch';
import Breadcrumb from './Components/Breadcrumb';
import NotFound from './Pages/NotFound';
import AdminDashboard from './Pages/AdminDashboard';
import AdminLogin from './Pages/AdminLogin';


function ScrollToSection() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return null;
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoaded');
    if (!hasLoadedBefore) {
      sessionStorage.setItem('hasLoaded', 'true');
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape') {
        setShowSearch(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <>
      {/* Global default SEO — overridden per route by child Meta components */}
      <Meta {...HOME_META} />

      <div className="min-h-screen bg-[#030014] dark:bg-[#030014] light:bg-gray-50 transition-colors duration-300">
      {isLoading && <PremiumLoadingScreen onLoadingComplete={handleLoadingComplete} />}
      
      {!isLoading && (
        <>
          <NeuralNetworkBackground />
          <AICodeMatrix />
          <FloatingAIBadges />
          
          <ParticleCursor />
          <ScrollToSection />
          <Navbar />
          <ThemeToggle />
          
          {showSearch && <AdvancedSearch onClose={() => setShowSearch(false)} />}
          
          {/* Breadcrumb Navigation */}
          <div className="container mx-auto px-4 pt-24">
            <Breadcrumb />
          </div>

          <Routes>
            <Route path="/" element={
              <PageTransition>
                {/* Home page sections with correct landmark IDs */}
                <section id="Home" aria-label="Hero section">
                  <Home />
                </section>
                <section id="About" aria-label="About Avansh Yadav">
                  <About />
                  <Tech />
                </section>
                <section id="experience" aria-label="Work experience and education">
                  <Experience />
                </section>
                <section id="Contact" aria-label="Contact Avansh Yadav">
                  <Form />
                </section>
              </PageTransition>
            } />

            {/* Projects List Page */}
            <Route path="/projects" element={
              <PageTransition>
                <Projects />
              </PageTransition>
            } />

            {/* Individual Project Detail Page */}
            <Route path="/project/:id" element={
              <PageTransition>
                <ProjectDetail />
              </PageTransition>
            } />

            {/* Admin Login — noindex */}
            <Route path="/admin/login" element={
              <PageTransition>
                <AdminLogin />
              </PageTransition>
            } />

            {/* Admin Dashboard — noindex */}
            <Route path="/admin" element={
              <PageTransition>
                <AdminDashboard />
              </PageTransition>
            } />

            {/* 404 Not Found Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>

          <Footer />
        </>
      )}
      </div>
    </>
  );
}

export default App;
